import {
  BanknotesIcon,
  BriefcaseIcon,
  BuildingStorefrontIcon,
  HomeIcon,
  TruckIcon,
  DocumentArrowUpIcon,
  Cog8ToothIcon,
  ClipboardDocumentListIcon,
  RadioIcon,
  UsersIcon,
  ArchiveBoxIcon,
  DocumentChartBarIcon,
} from "@heroicons/react/24/outline";
import { hasAnyStoredPermission, hasStoredPermission } from "../utils/authority";
import { PERMISSIONS } from "../constants/access-control";

/**
 * Builds the primary nav menu. Each entry can opt into permission-based
 * visibility by setting `authority` to a `hasStoredPermission(...)` result.
 *
 * The Dashboard menu reads `entry.authority` as a boolean and hides items
 * that resolve to `false`. Items without `authority` are visible to everyone
 * (gated only by feature flag / appType).
 */
export const getMenuConfig = (t) => [
  { title: t("dashboard.main"), path: "/", icon: HomeIcon },
  {
    title: t("jobs.main"),
    path: "/jobs",
    icon: BriefcaseIcon,
    authority: hasStoredPermission(PERMISSIONS.JOBS_READ),
    featureKey: "jobs.enabled",
  },
  {
    title: t("dispatch.main"),
    path: "/dispatch",
    icon: RadioIcon,
    authority: hasStoredPermission(PERMISSIONS.DISPATCH_READ),
    featureKey: "dispatch.enabled",
  },
  {
    title: t("invoices.main"),
    path: "/invoices",
    icon: BanknotesIcon,
    authority: hasStoredPermission(PERMISSIONS.INVOICES_READ),
    featureKey: "invoices.enabled",
  },
  {
    title: "Manifests",
    path: "/manifests",
    icon: ClipboardDocumentListIcon,
    authority: hasStoredPermission(PERMISSIONS.MANIFESTS_READ),
    appTypes: ["trucking"],
    featureKey: "manifests.enabled",
  },
  {
    title: t("payouts.main"),
    path: "/payouts",
    icon: DocumentArrowUpIcon,
    authority: hasStoredPermission(PERMISSIONS.PAYOUTS_READ),
    appTypes: ["trucking", "serviceRepair"],
    featureKey: "payouts.enabled",
  },
  {
    title: t("clients.main"),
    path: "/clients",
    icon: BuildingStorefrontIcon,
    authority: hasStoredPermission(PERMISSIONS.CLIENTS_READ),
    featureKey: "clients.enabled",
  },
  {
    title: t("drivers.main"),
    path: "/drivers",
    icon: TruckIcon,
    authority: hasStoredPermission(PERMISSIONS.DRIVERS_READ),
    appTypes: ["trucking"],
    featureKey: "drivers.enabled",
  },
  {
    title: t("employees.main"),
    path: "/employees",
    icon: UsersIcon,
    authority: hasStoredPermission(PERMISSIONS.EMPLOYEES_READ),
    appTypes: ["serviceRepair"],
    featureKey: "employees.enabled",
  },
  {
    title: "Reports",
    path: "/reports",
    icon: DocumentChartBarIcon,
    authority: hasStoredPermission(PERMISSIONS.REPORTS_READ),
    appTypes: ["trucking", "serviceRepair"],
    featureKey: "reports.enabled",
  },
  {
    title: "Inventory",
    path: "/inventory",
    icon: ArchiveBoxIcon,
    // Inventory permissions are retired. Visibility is driven by the
    // `inventory.enabled` feature flag only.
    appTypes: ["serviceRepair"],
    featureKey: "inventory.enabled",
  },
  {
    title: t("settings.main"),
    path: "/settings",
    icon: Cog8ToothIcon,
    authority: hasAnyStoredPermission([
      PERMISSIONS.SETTINGS_USERS_READ,
      PERMISSIONS.SETTINGS_USERS_MANAGE,
      PERMISSIONS.SETTINGS_ROLES_READ,
      PERMISSIONS.SETTINGS_ROLES_MANAGE,
      PERMISSIONS.SETTINGS_BILLING_READ,
      PERMISSIONS.SETTINGS_BILLING_MANAGE,
      PERMISSIONS.SETTINGS_TAGS_MANAGE,
      PERMISSIONS.SETTINGS_TEMPLATES_MANAGE,
      PERMISSIONS.SETTINGS_ORGANIZATION_MANAGE,
      PERMISSIONS.SETTINGS_ADVANCED_MANAGE,
    ]),
    featureKey: "settings.enabled",
  },
];
